<template>
  <div class="admin-panel task-page task-page--new-client">

    <!-- ── TOP BAR ───────────────────────────────────────────────── -->
    <admin-header
      variant="full"
      :show-back="true"
      :crumbs="[{ label: 'New Client' }]"
      :session-countdown="sessionCountdown"
      :formatted-countdown="formattedCountdown"
      @open-logout="openLogoutModal"
      @settings-saved="onSettingsSaved"
    />

    <!-- ── MAIN ──────────────────────────────────────────────────── -->
    <main class="ap-container ap-fade-in">

      <!-- Page hero -->
      <div class="tp-hero">
        <div class="tp-hero-icon">
          <fa-icon :icon="['fas', 'user-plus']" />
        </div>
        <div>
          <h1 class="tp-hero-title">New Client</h1>
          <p class="tp-hero-sub">Add a new client, import from Cosec List, or remove an existing account.</p>
        </div>
      </div>

      <!-- ── MANAGE CLIENTS ─────────────────────────────────────── -->
      <div class="ap-nc-wrap">
        <aside class="ap-panel ap-nc-panel">
          <div class="ap-panel-head">
            <h3>Manage Clients</h3>
          </div>

          <div class="ap-nc-layout">

            <!-- Left sidebar nav -->
            <nav class="ap-nc-sidebar">
              <button
                class="ap-nc-nav-item"
                :class="{ active: activeTab === 'add' }"
                @click="activeTab = 'add'"
              >
                <fa-icon :icon="['fas', 'user-plus']" class="ap-nc-nav-icon" />
                <div>
                  <div class="ap-nc-nav-label">Add Client</div>
                  <div class="ap-nc-nav-sub">Create new account</div>
                </div>
              </button>
              <button
                class="ap-nc-nav-item"
                :class="{ active: activeTab === 'import' }"
                @click="activeTab = 'import'; resetCosecImport(); fetchClients()"
              >
                <fa-icon :icon="['fas', 'file-excel']" class="ap-nc-nav-icon" />
                <div>
                  <div class="ap-nc-nav-label">Import Excel</div>
                  <div class="ap-nc-nav-sub">Bulk import Cosec List</div>
                </div>
              </button>
              <button
                class="ap-nc-nav-item"
                :class="{ active: activeTab === 'remove' }"
                @click="activeTab = 'remove'"
              >
                <fa-icon :icon="['fas', 'user-minus']" class="ap-nc-nav-icon" />
                <div>
                  <div class="ap-nc-nav-label">Remove Client</div>
                  <div class="ap-nc-nav-sub">Revoke access</div>
                </div>
              </button>
            </nav>

            <!-- Right content -->
            <div class="ap-nc-content">

            <!-- ADD FORM -->
            <form v-if="activeTab === 'add'" class="ap-nc-form" @submit.prevent="validateAndAdd">

              <div class="cdm-section">
                <!-- ── Section 1: Basic Info ──────────────────────── -->
                <div class="cdm-section-label">Basic Info</div>
                <div class="cdm-section-wrapper">

                  <text-input ref="fieldClient" v-model="newClient" label="Client's Full Name" :icon="['fas', 'building']" placeholder="e.g. Super Crop Sdn Bhd" :required="true" :uppercase="true" />
                  <p v-if="formErrors.client" class="ap-field-error">{{ formErrors.client }}</p>

                  <text-input ref="fieldInitial" v-model="newInitial" label="Initial" :icon="['fas', 'stamp']" placeholder="e.g. TYC" :required="true" :uppercase="true" />
                  <p v-if="formErrors.initial" class="ap-field-error">{{ formErrors.initial }}</p>

                  <select-input
                    ref="fieldClientType"
                    v-model="newClientType"
                    label="Business Type"
                    :required="true"
                    :options="clientTypeOptions"
                    :icon="['fas', 'building']"
                    placeholder="Select client type…"
                  />
                  <p v-if="formErrors.clientType" class="ap-field-error">{{ formErrors.clientType }}</p>

                  <div ref="fieldService" class="ap-field">
                    <label>Service <span class="ap-req">*</span></label>
                    <div class="ap-checkbox-group">
                      <label
                        v-for="svc in serviceCategories"
                        :key="svc.value"
                        class="ap-checkbox-item"
                        :class="['ap-checkbox-item--' + svc.value, { 'is-checked': newServiceCategories.includes(svc.value) }]"
                      >
                        <input type="checkbox" :value="svc.value" v-model="newServiceCategories" style="display:none" />
                        {{ svc.name }}
                      </label>
                    </div>
                    <p v-if="formErrors.service" class="ap-field-error" style="margin-top:6px">{{ formErrors.service }}</p>
                  </div>

                  <select-input
                    v-model="newStatus"
                    label="Status"
                    :options="statusOptions"
                    :icon="['fas', 'toggle-on']"
                    placeholder="Select status…"
                  />
                </div>
              </div>

              <div class="cdm-section">
              <!-- ── Section 2: Statutory Info ─────────────────── -->
                <div class="cdm-section-label">Statutory Info</div>
                <div class="cdm-section-wrapper">

                  <template v-if="newClientType !== 'salaried_person'">
                    <text-input v-model="newSsmNumberNew" label="SSM Number (New)" :icon="['fas', 'id-card']" placeholder="e.g. 202301012345" />
                    <text-input v-model="newSsmNumberOld" label="SSM Number (Old)" :icon="['fas', 'id-card']" placeholder="e.g. 1234567-A" />
                  </template>

                  <tax-input v-model="newTaxNumber" label="Tax Number" />

                  <prefix-input v-if="newClientType !== 'salaried_person'" v-model="newENumber" label="E Number" prefix="E" placeholder="e.g. 0012345678" />

                  <select-input
                    v-model="newIrbBranch"
                    label="IRB Branch"
                    :options="irbBranchOptions"
                    :icon="['fas', 'landmark']"
                    placeholder="Select branch…"
                  />
                  <select-input
                    v-if="!['salaried_person', 'partnership', 'sole_prop', 'llp'].includes(newClientType)"
                    v-model="newAuditor"
                    label="Auditor"
                    :options="auditorOptions"
                    :icon="['fas', 'user-tie']"
                    placeholder="Select auditor…"
                  />

                  <select-input
                    v-model="newYearEnd"
                    label="Year End"
                    :options="yearEndOptions"
                    :icon="['fas', 'calendar-alt']"
                    placeholder="Select month…"
                  />
                </div>
              </div>

              <!-- ── Section 3: Address ─────────────────────────── -->
              <div class="cdm-section">
                <div class="cdm-section-label">Address</div>
                <div class="cdm-section-wrapper">

                  <textarea-input v-model="newBusinessAddress" label="Business Address" placeholder="e.g. 123 Jalan Bukit Bintang, 55100 Kuala Lumpur" :uppercase="true"/>
                  <textarea-input v-model="newMailingAddress" label="Mailing Address" placeholder="Same as business address, or P.O. Box…" :uppercase="true"/>
                </div>
              </div>

              <!-- ── Section 4: Important Dates ───────────────── -->
              <template v-if="newServiceCategories.includes('cosec') || newServiceCategories.includes('payroll_monthly')">
                <div class="cdm-section">
                  <div class="cdm-section-label">Important Dates</div>
                  <div class="cdm-section-wrapper">
                    <template v-if="newServiceCategories.includes('cosec')">
                      <date-input v-model="newIncorporationDate" label="Incorporation Date" :max-date="newInceptionDate" />
                      <date-input v-model="newInceptionDate" label="Inception Date" :min-date="newIncorporationDate" />
                      <select-input
                        v-model="newCosecTerminationType"
                        label="Cosec Termination Type"
                        :options="cosecTerminationTypeOptions"
                        :icon="['fas', 'sign-out-alt']"
                        placeholder="Select type…"
                      />
                      <date-input v-model="newCosecTerminationDate" label="Cosec Termination Date" />
                    </template>

                    <template v-if="newServiceCategories.includes('payroll_monthly')">
                      <select-input
                        v-model="newPayday"
                        label="Payday"
                        :options="paydayOptions"
                        :icon="['fas', 'calendar-alt']"
                        placeholder="Select payday…"
                      />
                    </template>
                  </div>
                </div>
              </template>

              <!-- ── Section 5: Contacts ────────────────────────── -->
              <div class="cdm-section">
                <div class="cdm-section-label">Contacts</div>
                <div class="cdm-section-wrapper">

                  <div ref="fieldContacts" class="ap-field">
                    <contact-cards v-model="newContactCards" :min-cards="1" :label-options="contactCategoryOptions" :title-options="contactTitleOptions" :card-errors="contactCardErrors" />
                    <p v-if="formErrors.contacts" class="ap-field-error" style="margin-top:6px">{{ formErrors.contacts }}</p>
                  </div>
                </div>
              </div>

              <div v-if="generatedUsername" class="ap-preview ap-preview-col">
                <div class="ap-preview-row">
                  <fa-icon :icon="['fas', 'user']" class="ap-preview-icon" />
                  <span>Username</span>
                  <code>{{ generatedUsername }}</code>
                </div>
                <div class="ap-preview-row">
                  <fa-icon :icon="['fas', 'key']" class="ap-preview-icon" />
                  <span>Password</span>
                  <code>{{ generatedPassword }}</code>
                </div>
              </div>

              <app-button
                type="submit"
                variant="primary"
                size="lg"
                team="admin"
                :disabled="addingClient"
                style="margin-top: 18px; width: 100%"
              >
                <fa-icon :icon="addingClient ? ['fas', 'spinner'] : ['fas', 'paper-plane']" :spin="addingClient" />
                {{ addingClient ? 'Creating…' : 'Create & send invite' }}
              </app-button>
            </form>

            <!-- IMPORT XLSX -->
            <div v-if="activeTab === 'import'" class="ap-nc-form">
              <p class="ap-import-hint">Upload a Cosec List (.xlsx) to auto-import company and shareholder data.</p>

              <!-- Drop zone -->
              <div
                class="ap-xlsx-drop"
                :class="{ 'is-over': cosecIsDragging }"
                @click="$refs.cosecFile.click()"
                @dragover.prevent="cosecIsDragging = true"
                @dragleave.prevent="cosecIsDragging = false"
                @drop.prevent="onCosecDrop"
              >
                <fa-icon :icon="['fas', cosecParsing ? 'spinner' : 'file-excel']" :spin="cosecParsing" class="ap-xlsx-icon" />
                <span>{{ cosecParsing ? 'Parsing…' : 'Click or drag .xlsx here' }}</span>
              </div>
              <input ref="cosecFile" type="file" accept=".xlsx,.xls" style="display:none" @change="onCosecFileChange" />

              <p v-if="cosecParseError" class="ap-xlsx-err">
                <fa-icon :icon="['fas', 'exclamation-circle']" /> {{ cosecParseError }}
              </p>

              <!-- Company list -->
              <div v-if="cosecCompanies.length" class="ap-xlsx-list">
                <p class="ap-xlsx-found">{{ cosecCompanies.length }} compan{{ cosecCompanies.length === 1 ? 'y' : 'ies' }} found</p>
                <button
                  v-for="(co, i) in cosecCompanies"
                  :key="i"
                  class="ap-xlsx-co-btn"
                  :class="{ active: cosecSelected === co }"
                  @click="cosecSelected = co; cosecSaveDone = false; cosecSaveError = ''"
                >
                  <fa-icon :icon="['fas', 'building']" class="ap-xlsx-co-icon" />
                  <span>{{ co.client }}</span>
                  <fa-icon :icon="['fas', 'chevron-right']" class="ap-xlsx-co-arr" />
                </button>
              </div>

              <!-- Import preview (shown below company list when one is selected) -->
              <div v-if="cosecSelected" class="ap-import-preview ap-import-preview-inline">

                <div class="ap-nc-preview-head">
                  <button class="ap-modal-close" @click="cosecSelected = null" title="Back to list">
                    <fa-icon :icon="['fas', 'arrow-left']" />
                  </button>
                  <span>Import Preview — {{ cosecSelected.client }}</span>
                </div>

                <!-- Company card -->
                <div class="ap-ic-card">
                  <div class="ap-ic-card-head">
                    <fa-icon :icon="['fas', 'building']" class="ap-ic-head-icon" />
                    <span>Company Information</span>
                  </div>
                  <div class="ap-ic-grid">
                    <div class="ap-ic-row"><span class="ap-ic-label">Client</span><span class="ap-ic-val">{{ cosecSelected.client }}</span></div>
                    <div v-if="cosecSelected.company_name_explanation" class="ap-ic-row ap-ic-row-full"><span class="ap-ic-label">Name Explanation</span><span class="ap-ic-val">{{ cosecSelected.company_name_explanation }}</span></div>
                    <div class="ap-ic-row ap-ic-row-full"><span class="ap-ic-label">Nature of Business</span><span class="ap-ic-val">{{ cosecSelected.nature_of_business || '—' }}</span></div>
                    <div class="ap-ic-row ap-ic-row-full"><span class="ap-ic-label">Business Address</span><span class="ap-ic-val">{{ cosecSelected.business_address || '—' }}</span></div>
                    <div class="ap-ic-row"><span class="ap-ic-label">Phone</span><span class="ap-ic-val">{{ cosecSelected.business_phone || '—' }}</span></div>
                    <div class="ap-ic-row"><span class="ap-ic-label">Email</span><span class="ap-ic-val">{{ cosecSelected.business_email || '—' }}</span></div>
                    <div class="ap-ic-row"><span class="ap-ic-label">Initial Capital</span><span class="ap-ic-val">RM {{ cosecSelected.initial_capital }}</span></div>
                    <div class="ap-ic-row"><span class="ap-ic-label">Price / Share</span><span class="ap-ic-val">RM {{ cosecSelected.price_per_share }}</span></div>
                    <div class="ap-ic-row"><span class="ap-ic-label">Total Shares</span><span class="ap-ic-val">{{ cosecSelected.total_shares }}</span></div>
                    <div class="ap-ic-row"><span class="ap-ic-label">Year End</span><span class="ap-ic-val">{{ cosecSelected.year_end || '—' }}</span></div>
                  </div>
                </div>

                <!-- Directors -->
                <div class="ap-ic-card" v-if="cosecPersonsByRole(cosecSelected, 'director').length">
                  <div class="ap-ic-card-head">
                    <fa-icon :icon="['fas', 'user-tie']" class="ap-ic-head-icon" />
                    <span>Person in Charge (Directors)</span>
                    <span class="ap-ic-badge ap-ic-badge-required">Required</span>
                  </div>
                  <div
                    v-for="(dir, di) in cosecPersonsByRole(cosecSelected, 'director')"
                    :key="'dir-' + di"
                    class="ap-ic-person"
                    :class="{ 'ap-ic-person-primary': di === 0 }"
                  >
                    <div class="ap-ic-person-head">
                      <div class="ap-ic-avatar">{{ dir.full_name ? dir.full_name.charAt(0).toUpperCase() : '?' }}</div>
                      <div>
                        <div class="ap-ic-person-name">{{ dir.full_name }} <span v-if="di === 0" class="ap-ic-tag">Primary</span></div>
                        <div class="ap-ic-person-sub">Director {{ dir.sort_order }}</div>
                      </div>
                    </div>
                    <div class="ap-ic-grid">
                      <div class="ap-ic-row"><span class="ap-ic-label">Nationality</span><span class="ap-ic-val">{{ dir.nationality || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Gender</span><span class="ap-ic-val">{{ dir.gender || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">NRIC</span><span class="ap-ic-val ap-mono">{{ dir.nric_number || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Passport</span><span class="ap-ic-val ap-mono">{{ dir.passport_number || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Phone</span><span class="ap-ic-val">{{ dir.phone || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Email</span><span class="ap-ic-val">{{ dir.email || '—' }}</span></div>
                      <div class="ap-ic-row ap-ic-row-full"><span class="ap-ic-label">Address</span><span class="ap-ic-val">{{ dir.residential_address || '—' }}</span></div>
                    </div>
                  </div>
                </div>

                <!-- Shareholders -->
                <div class="ap-ic-card" v-if="cosecPersonsByRole(cosecSelected, 'shareholder').length">
                  <div class="ap-ic-card-head">
                    <fa-icon :icon="['fas', 'users']" class="ap-ic-head-icon" />
                    <span>Shareholders</span>
                    <span class="ap-ic-badge">{{ cosecPersonsByRole(cosecSelected, 'shareholder').length }} person{{ cosecPersonsByRole(cosecSelected, 'shareholder').length > 1 ? 's' : '' }}</span>
                  </div>
                  <div v-for="(sh, si) in cosecPersonsByRole(cosecSelected, 'shareholder')" :key="'sh-' + si" class="ap-ic-person">
                    <div class="ap-ic-person-head">
                      <div class="ap-ic-avatar ap-ic-avatar-sh">{{ sh.full_name ? sh.full_name.charAt(0).toUpperCase() : '?' }}</div>
                      <div>
                        <div class="ap-ic-person-name">{{ sh.full_name }}</div>
                        <div class="ap-ic-person-sub">Shareholder {{ sh.sort_order }} · {{ sh.shares_held || '?' }} shares</div>
                      </div>
                      <span v-if="sh.is_also_director === 'Yes'" class="ap-ic-tag ap-ic-tag-dir">Also Director</span>
                    </div>
                    <div class="ap-ic-grid">
                      <div class="ap-ic-row"><span class="ap-ic-label">Nationality</span><span class="ap-ic-val">{{ sh.nationality || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Gender</span><span class="ap-ic-val">{{ sh.gender || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">NRIC</span><span class="ap-ic-val ap-mono">{{ sh.nric_number || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Passport</span><span class="ap-ic-val ap-mono">{{ sh.passport_number || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Phone</span><span class="ap-ic-val">{{ sh.phone || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Email</span><span class="ap-ic-val">{{ sh.email || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Shares</span><span class="ap-ic-val">{{ sh.shares_held || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Source of Funds</span><span class="ap-ic-val">{{ sh.source_of_funds || '—' }}</span></div>
                    </div>
                  </div>
                </div>

                <!-- Next of Kin -->
                <div class="ap-ic-card" v-if="cosecPersonsByRole(cosecSelected, 'next_of_kin').length">
                  <div class="ap-ic-card-head">
                    <fa-icon :icon="['fas', 'heart']" class="ap-ic-head-icon" />
                    <span>Next of Kin (Sole Director)</span>
                  </div>
                  <div v-for="(nok, ni) in cosecPersonsByRole(cosecSelected, 'next_of_kin')" :key="'nok-' + ni" class="ap-ic-person">
                    <div class="ap-ic-person-head">
                      <div class="ap-ic-avatar ap-ic-avatar-nok">{{ nok.full_name ? nok.full_name.charAt(0).toUpperCase() : '?' }}</div>
                      <div>
                        <div class="ap-ic-person-name">{{ nok.full_name }}</div>
                        <div class="ap-ic-person-sub">{{ nok.relationship || 'Next of Kin' }}</div>
                      </div>
                    </div>
                    <div class="ap-ic-grid">
                      <div class="ap-ic-row"><span class="ap-ic-label">NRIC</span><span class="ap-ic-val ap-mono">{{ nok.nric_number || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Phone</span><span class="ap-ic-val">{{ nok.phone || '—' }}</span></div>
                      <div class="ap-ic-row"><span class="ap-ic-label">Email</span><span class="ap-ic-val">{{ nok.email || '—' }}</span></div>
                      <div class="ap-ic-row ap-ic-row-full"><span class="ap-ic-label">Address</span><span class="ap-ic-val">{{ nok.residential_address || '—' }}</span></div>
                    </div>
                  </div>
                </div>

                <!-- Save action -->
                <div class="ap-ic-actions">
                  <p v-if="cosecSaveError" class="ap-ic-err"><fa-icon :icon="['fas', 'exclamation-circle']" /> {{ cosecSaveError }}</p>

                  <div v-if="cosecSaveDone" class="ap-ic-success">
                    <fa-icon :icon="['fas', 'check-circle']" /> Data saved and linked to client successfully.
                  </div>

                  <template v-else>
                    <div class="ap-client-select-wrap">
                      <label class="ap-field-label">Link to client account</label>
                      <div v-if="clientsLoading" class="ap-clients-loading">
                        <fa-icon :icon="['fas', 'spinner']" :spin="true" /> Loading clients…
                      </div>
                      <select v-else v-model="selectedClientId" class="ap-client-select">
                        <option :value="null" disabled>Select a client…</option>
                        <option v-for="c in clients" :key="c.id" :value="c.id">
                          {{ c.client }}
                        </option>
                      </select>
                    </div>

                    <button class="ap-btn ap-btn-primary ap-btn-lg" :disabled="cosecSaving || !selectedClientId" @click="saveCosecCompany">
                      <fa-icon :icon="cosecSaving ? ['fas', 'spinner'] : ['fas', 'database']" :spin="cosecSaving" />
                      {{ cosecSaving ? 'Saving…' : 'Save to Database' }}
                    </button>
                  </template>
                </div>

              </div>
            </div>

            <!-- REMOVE FORM -->
            <div v-if="activeTab === 'remove'" class="ap-nc-form">
              <text-input v-model="removeUsername" label="Client username" :icon="['fas', 'user']" placeholder="e.g. super_crop_sdn_bhd" :required="true" hint="This action revokes account access immediately." />
              <div class="ap-danger-notice">
                <fa-icon :icon="['fas', 'exclamation-circle']" class="ap-notice-icon" />
                <div>Removing a client cannot be undone. Their documents will remain archived for 7 years per MIA requirements.</div>
              </div>
              <button
                class="ap-btn ap-btn-danger ap-btn-lg ap-btn-block"
                style="margin-top: 18px"
                :disabled="!removeUsername.trim()"
                @click="removeClient"
              >
                <fa-icon :icon="['fas', 'user-minus']" /> Remove client
              </button>
            </div>

            </div><!-- end ap-nc-content -->
          </div><!-- end ap-nc-layout -->
        </aside>
      </div>

    </main>

    <!-- ── MODALS ─────────────────────────────────────────────────── -->
    <alert-modal :show="showPopup" :message="popupMessage" :status="popupStatus" @close="closePopup" />

    <session-modal :show="showAdminSessionModal" @continue="continueAdminSession" @signout="signOutAdminSession">
      Your admin session has expired. Continue to refresh your session or sign out.
    </session-modal>

    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="closeLogoutModal"
      @confirm="confirmLogout"
    >Are you sure you want to logout?</confirm-modal>

    <!-- Page-blocking loading overlay -->
    <transition name="ap-overlay-fade">
      <div v-if="addingClient" class="ap-page-overlay">
        <div class="ap-page-overlay-box">
          <fa-icon :icon="['fas', 'spinner']" spin class="ap-overlay-spinner" />
          <div class="ap-overlay-title">Creating Client</div>
          <div class="ap-overlay-sub">Setting up account…</div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import adminSessionMixin from '@/mixins/adminSessionMixin'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import { yearEndOptions, createStatusOptions as statusOptions } from '@/utils/clientOptions'
import DateInput from '@/components/form/DateInput.vue'
import TextInput from '@/components/form/TextInput.vue'
import TextareaInput from '@/components/form/TextareaInput.vue'
import SelectInput from '@/components/form/SelectInput.vue'
import ContactCards from '@/components/form/ContactCards.vue'
import TaxInput from '@/components/form/TaxInput.vue'
import PrefixInput from '@/components/form/PrefixInput.vue'
import AppButton from '@/components/shared/AppButton.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import AlertModal from '@/components/shared/AlertModal.vue'
import { parseCosecXlsx } from '@/utils/cosecParser'

export default {
  name: 'admin-new-client',
  mixins: [adminSessionMixin],
  components: { AdminHeader, AppButton, DateInput, TextInput, TextareaInput, SelectInput, ContactCards, TaxInput, PrefixInput, ConfirmModal, SessionModal, AlertModal },
  metaInfo () {
    return {
      title: 'Tyng & Co | New Client',
      titleTemplate: null
    }
  },
  data () {
    return {
      activeTab: 'add',
      // Add form
      newClient: '',
      newInitial: '',
      newClientType: '',
      newYearEnd: '',
      newStatus: 'Active',
      newServiceCategories: [],
      newTaxNumber: '',
      newENumber: '',
      newIrbBranch: '',
      newAuditor: '',
      newBusinessAddress: '',
      newMailingAddress: '',
      irbBranches: ['Cheras', 'W.P. Kuala Lumpur', 'Bukit Mertajam', 'Petaling Jaya', 'Kuala Lumpur Bandar'],
      auditors: ['Marie J & Co', 'Cheng & Co', 'YYC PLT'],
      contactCategories: ['Internal Staff', 'Outsourced Account', 'Director', 'Shareholder'],
      contactTitles: ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', "Dato'", 'Datin', 'Tan Sri', 'Puan Sri'],
      newSsmNumberNew: '',
      newSsmNumberOld: '',
      newIncorporationDate: '',
      newInceptionDate: '',
      newCosecTerminationType: '',
      newCosecTerminationDate: '',
      newPayday: '',
      cosecTerminationTypeOptions: [
        { value: 'Resigned', label: 'Resigned' },
        { value: 'Vacated', label: 'Vacated' }
      ],
      newContactCards: [{ label: '', title: '', name: '', phone: '', email: '', roles: [] }],
      serviceCategories: [
        { name: 'Tax', value: 'tax' },
        { name: 'TP Reminder', value: 'tp_reminder' },
        { name: 'Cosec', value: 'cosec' },
        { name: 'Incorporation', value: 'incorporation' },
        { name: 'Payroll Yearly', value: 'payroll_yearly' },
        { name: 'Payroll Reminder', value: 'payroll_reminder' },
        { name: 'Payroll Monthly', value: 'payroll_monthly' },
        { name: 'Accounting', value: 'accounting' }
      ],
      clientTypeOptions: [
        { value: 'sdn_bhd', label: 'Sdn Bhd' },
        { value: 'llp', label: 'LLP' },
        { value: 'sole_prop', label: 'Sole Prop' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'salaried_person', label: 'Salaried Person' }
      ],
      yearEndOptions,
      statusOptions,
      // Remove form
      removeUsername: '',
      // Loading
      addingClient: false,
      // Form errors
      formErrors: { client: '', initial: '', clientType: '', service: '', payday: '', contacts: '' },
      contactCardErrors: [],
      // Popup
      showPopup: false,
      popupMessage: '',
      popupActionType: '',
      popupStatus: 'success',
      // XLSX import
      cosecCompanies: [],
      cosecParsing: false,
      cosecParseError: '',
      cosecSelected: null,
      cosecSaving: false,
      cosecSaveError: '',
      cosecSaveDone: false,
      cosecIsDragging: false,
      // Client selector for import
      clients: [],
      clientsLoading: false,
      selectedClientId: null
    }
  },
  computed: {
    irbBranchOptions () {
      return this.irbBranches.map(b => ({ value: b, label: b }))
    },
    auditorOptions () {
      return this.auditors.map(a => ({ value: a, label: a }))
    },
    contactCategoryOptions () {
      return this.contactCategories.map(c => ({ value: c, label: c }))
    },
    contactTitleOptions () {
      return this.contactTitles.map(t => ({ value: t, label: t }))
    },
    paydayOptions () {
      return Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))
    },
    generatedUsername () {
      if (!this.newClient.trim()) return ''
      const slug = this.newClient.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
      return slug || ''
    },
    generatedPassword () {
      if (!this.newClient.trim()) return ''
      const slug = this.newClient.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
      return slug ? 'password_' + slug : ''
    },
    canAddClient () {
      const contactCardsValid = this.newContactCards.every(
        c => !c.email.trim() || this.isValidEmailFormat(c.email)
      )
      const hasContactName = this.newContactCards.some(c => c.name.trim())
      return (
        this.newClient.trim() &&
        hasContactName &&
        this.newClientType &&
        this.newYearEnd &&
        this.newServiceCategories.length > 0 &&
        this.generatedUsername &&
        contactCardsValid &&
        this.newClient.length >= 3 &&
        this.newClient.length <= 50
      )
    }
  },
  watch: {
    showPopup () { this.toggleBodyScroll() },
    showLogoutModal () { this.toggleBodyScroll() },
    showAdminSessionModal () { this.toggleBodyScroll() }
  },
  mounted () {
    this.startSessionTimer()
    this.fetchAdminSettings()
  },
  methods: {
    validateAndAdd () {
      if (this.validateForm()) {
        this.addClient()
      } else {
        this.$nextTick(() => this.scrollToFirstError())
      }
    },

    scrollToFirstError () {
      const priority = [
        { key: 'client', ref: 'fieldClient' },
        { key: 'initial', ref: 'fieldInitial' },
        { key: 'clientType', ref: 'fieldClientType' },
        { key: 'service', ref: 'fieldService' },
        { key: 'payday', ref: 'fieldPayday' },
        { key: 'contacts', ref: 'fieldContacts' }
      ]
      for (const { key, ref } of priority) {
        if (this.formErrors[key]) {
          const r = this.$refs[ref]
          if (r) {
            const el = r.$el || r
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
          break
        }
      }
    },

    validateForm () {
      const errors = { client: '', initial: '', clientType: '', service: '', payday: '', contacts: '' }
      let valid = true

      const clientName = this.newClient.trim()
      if (!clientName || clientName.length < 3 || clientName.length > 50) {
        errors.client = "please enter client's full name"
        valid = false
      }
      if (!this.newInitial.trim()) {
        errors.initial = 'please enter initial'
        valid = false
      }
      if (!this.newClientType) {
        errors.clientType = 'please select a business type'
        valid = false
      }
      if (this.newServiceCategories.length === 0) {
        errors.service = 'please select minimum one service'
        valid = false
      }

      const cards = this.newContactCards
      const cardErrors = cards.map(() => ({ label: false, name: false, phone: false, email: false, emailGroup: false }))
      let firstContactError = ''

      if (cards.length === 0) {
        errors.contacts = 'please add at least one contact'
        valid = false
      } else if (!cards.some(c => c.roles.includes('pic'))) {
        errors.contacts = 'please select one contact as PIC'
        cardErrors.forEach(e => { e.emailGroup = true })
        valid = false
      } else {
        for (let i = 0; i < cards.length; i++) {
          const card = cards[i]
          const isPic = card.roles.includes('pic')
          if (isPic) {
            if (!card.name.trim()) {
              cardErrors[i].name = true
              if (!firstContactError) firstContactError = 'please enter full name for PIC'
              valid = false
            }
            const picPhone = (card.phone || '').replace(/\D/g, '')
            if (!picPhone || picPhone.length < 7) {
              cardErrors[i].phone = true
              if (!firstContactError) firstContactError = 'please enter phone for PIC'
              valid = false
            } else if (picPhone.length > 15) {
              cardErrors[i].phone = true
              if (!firstContactError) firstContactError = 'please enter a valid phone number for PIC'
              valid = false
            }
            if (!card.email.trim()) {
              cardErrors[i].email = true
              if (!firstContactError) firstContactError = 'please enter email for PIC'
              valid = false
            } else if (!this.isValidEmailFormat(card.email)) {
              cardErrors[i].email = true
              if (!firstContactError) firstContactError = 'please enter a valid email for PIC'
              valid = false
            }
            if (!card.label) {
              cardErrors[i].label = true
              if (!firstContactError) firstContactError = 'please select contact category for PIC'
              valid = false
            }
          } else {
            if (!card.label) {
              cardErrors[i].label = true
              if (!firstContactError) firstContactError = 'please select contact category'
              valid = false
            }
            if (!card.email.trim()) {
              cardErrors[i].email = true
              if (!firstContactError) firstContactError = 'please enter email for contact'
              valid = false
            } else if (!this.isValidEmailFormat(card.email)) {
              cardErrors[i].email = true
              if (!firstContactError) firstContactError = 'please enter a valid email address'
              valid = false
            }
            if (!card.roles.some(r => ['to', 'cc', 'bcc', 'director'].includes(r))) {
              cardErrors[i].emailGroup = true
              if (!firstContactError) firstContactError = 'please select at least one email group'
              valid = false
            }
          }
        }
        if (!valid && firstContactError) errors.contacts = firstContactError
      }

      this.formErrors = errors
      this.contactCardErrors = cardErrors
      return valid
    },

    parseDMY (str) {
      if (!str) return null
      const [d, m, y] = str.split('/')
      return (d && m && y) ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : null
    },
    buildCategoryPayload () {
      const ye = parseInt(this.newYearEnd)
      const inc = this.parseDMY(this.newIncorporationDate)
      const LAST = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

      // Add n months to a base month (1-12), return { month, day: last-day }
      const addMo = (base, n) => {
        const m = ((base - 1 + n) % 12 + 12) % 12 + 1
        return { month: m, day: LAST[m] }
      }

      // Shift a specific month/day by n calendar days using the current year,
      // so Feb is correctly 28 or 29 days depending on whether this is a leap year.
      const shiftDays = (month, day, n) => {
        const d = new Date(new Date().getFullYear(), month - 1, day)
        d.setDate(d.getDate() + n)
        return { month: d.getMonth() + 1, day: d.getDate() }
      }

      const result = []
      const initStatus = { prev: 'pending', current: 'pending', next: 'pending' }

      const incDate = inc ? new Date(inc) : null
      const incMonth = incDate ? incDate.getMonth() + 1 : null
      const incDay = incDate ? incDate.getDate() : null

      if (this.newServiceCategories.includes('tax')) {
        result.push({
          id: 1,
          name: 'tax',
          display_name: 'Tax',
          sub_category: ye ? (() => {
            const tax8 = addMo(ye, 8)
            const mitrs = shiftDays(tax8.month, tax8.day, 30)
            const sub1m = addMo(ye, -1)
            const sub30d = shiftDays(ye, LAST[ye], -30)
            // sub1m.month > ye means it crossed into the previous year (e.g. ye=Jan → sub1m=Dec),
            // making it unconditionally earlier than sub30d which stays within the same year.
            const cp204 = sub1m.month > ye || (sub1m.month * 100 + sub1m.day) <= (sub30d.month * 100 + sub30d.day) ? sub1m : sub30d
            return [
              { name: 'tax_filing', display_name: 'Tax Filing', date: tax8, status: { ...initStatus } },
              { name: 'mitrs', display_name: 'MITRS', date: mitrs, status: { ...initStatus } },
              { name: 'cp204', display_name: 'CP204', date: cp204, status: { ...initStatus } },
              { name: 'cp204a6', display_name: 'CP204A(6)', date: addMo(ye, 6), status: { ...initStatus } },
              { name: 'cp204a9', display_name: 'CP204A(9)', date: addMo(ye, 9), status: { ...initStatus } },
              { name: 'cp204a11', display_name: 'CP204A(11)', date: addMo(ye, 11), status: { ...initStatus } }
            ]
          })() : []
        })
      }

      if (this.newServiceCategories.includes('tp_reminder')) {
        result.push({
          id: 5,
          name: 'tp_reminder',
          display_name: 'TP Reminder',
          sub_category: ye
            ? [{ name: 'tp_reminder', display_name: 'TP Reminder', date: addMo(ye, 1), status: { ...initStatus } }]
            : []
        })
      }

      if (this.newServiceCategories.includes('cosec')) {
        // compliance > ar = incorporation_date + 29 days
        const arDate = (incMonth && incDay) ? shiftDays(incMonth, incDay, 29) : null

        // compliance > fs (renamed from afs) = year_end + 7 months last-day
        const fsDate = ye ? addMo(ye, 7) : null

        // reminder: ar-based (from incorporation_date)
        const arReminder1st = incMonth ? addMo(incMonth, -5) : null
        const arReminder2nd = (incMonth && incDay) ? { month: incMonth, day: incDay } : null
        const arReminderLate1 = (incMonth && incDay) ? shiftDays(incMonth, incDay, 45) : null
        const arReminderLate2 = (incMonth && incDay) ? shiftDays(incMonth, incDay, 90) : null

        // reminder: afs-based (from year_end and fs date)
        const afsReminder1st = ye ? shiftDays(ye, LAST[ye], 1) : null
        const afsReminder2nd = ye ? addMo(ye, 5) : null
        const afsReminderLate1 = fsDate ? shiftDays(fsDate.month, fsDate.day, 45) : null
        const afsReminderLate2 = fsDate ? shiftDays(fsDate.month, fsDate.day, 90) : null

        result.push({
          id: 2,
          name: 'cosec',
          display_name: 'Cosec',
          sub_category: [
            {
              name: 'compliance',
              display_name: 'Compliance',
              tasks: [
                { name: 'ar', display_name: 'AR', date: arDate },
                { name: 'fs', display_name: 'FS', date: fsDate }
              ]
            },
            {
              name: 'reminder',
              display_name: 'Reminder',
              tasks: [
                { name: 'ar_reminder_1st', display_name: 'AR Reminder (1st)', date: arReminder1st },
                { name: 'ar_reminder_2nd', display_name: 'AR Reminder (2nd)', date: arReminder2nd },
                { name: 'ar_reminder_late_1', display_name: 'AR Reminder (Late) 1', date: arReminderLate1 },
                { name: 'ar_reminder_late_2', display_name: 'AR Reminder (Late) 2', date: arReminderLate2 },
                { name: 'afs_reminder_1st', display_name: 'AFS Reminder (1st)', date: afsReminder1st },
                { name: 'afs_reminder_2nd', display_name: 'AFS Reminder (2nd)', date: afsReminder2nd },
                { name: 'afs_reminder_late_1', display_name: 'AFS Reminder (Late 1)', date: afsReminderLate1 },
                { name: 'afs_reminder_late_2', display_name: 'AFS Reminder (Late 2)', date: afsReminderLate2 }
              ]
            }
          ]
        })
      }

      if (this.newServiceCategories.includes('incorporation')) {
        result.push({
          id: 8,
          name: 'Incorporation',
          display_name: 'Incorporation',
          sub_category: [
            { name: 'submit_S58_236', display_name: 'Submit S58 & 236(2)', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 13) : null },
            { name: 'submit_S472_473', display_name: 'Submit S47(2) / S47(3)', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 13) : null },
            { name: 'submit_beneficial_ownership', display_name: 'Submit Beneficial Ownership', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 59) : null },
            { name: 'pay_form_m1_stamp_duty', display_name: 'Pay Form M1 Stamp Duty', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 29) : null },
            { name: 'pay_form_m3_stamp_duty', display_name: 'Pay Form M3 Stamp Duty', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 29) : null },
            { name: 'order_company_stamp', display_name: 'Order Company Stamp', status: 'pending' },
            { name: 'create_superior_file', display_name: 'Create Superior File', status: 'pending' },
            { name: 'create_physical_file', display_name: 'Create Physical File', status: 'pending' },
            { name: 'submit_form_1st_cp204', display_name: 'Submit Form 1st CP204', status: 'pending' }
          ]
        })
      }

      if (this.newServiceCategories.includes('payroll_yearly')) {
        result.push({
          id: 30,
          name: 'payroll_yearly',
          display_name: 'Payroll Yearly',
          sub_category: [
            { name: 'form_ea', display_name: 'Form EA', date: { day: 28, month: 2 } },
            { name: 'form_e', display_name: 'Form E', date: { day: 30, month: 4 } }
          ]
        })
      }

      if (this.newServiceCategories.includes('payroll_reminder')) {
        result.push({
          id: 31,
          name: 'payroll_reminder',
          display_name: 'Payroll Reminder',
          sub_category: [
            { name: 'payroll_reminder', display_name: 'Payroll Reminder', date: { day: 31, month: 1 } }
          ]
        })
      }

      if (this.newServiceCategories.includes('payroll_monthly')) {
        const payday = parseInt(this.newPayday) || null
        result.push({
          id: 32,
          name: 'payroll_monthly',
          display_name: 'Payroll Monthly',
          sub_category: [
            { name: 'payslip', display_name: 'Payslip', date: { day: 7 } },
            { name: 'pcb_epf_socso_eis_hrdc', display_name: 'PCB/EPF/SOCSO/EIS/HRDC', date: { day: 15 } },
            { name: 'payday', display_name: 'Payday', date: payday ? { day: payday } : null }
          ]
        })
      }

      if (this.newServiceCategories.includes('accounting')) {
        result.push({
          id: 4,
          name: 'accounting',
          display_name: 'Accounting',
          sub_category: ye ? [
            { name: 'sst1', display_name: 'SST 1', date: addMo(ye, 1) },
            { name: 'sst2', display_name: 'SST 2', date: addMo(ye, 3) },
            { name: 'sst3', display_name: 'SST 3', date: addMo(ye, 5) },
            { name: 'sst4', display_name: 'SST 4', date: addMo(ye, 7) },
            { name: 'sst5', display_name: 'SST 5', date: addMo(ye, 9) },
            { name: 'sst6', display_name: 'SST 6', date: addMo(ye, 11) }
          ] : []
        })
      }

      return result
    },
    toggleBodyScroll () {
      const open = this.showPopup || this.showLogoutModal || this.showAdminSessionModal
      document.body.style.overflow = open ? 'hidden' : ''
    },
    async addClient () {
      this.addingClient = true
      try {
        const allCards = this.newContactCards.filter(c => c.name.trim() || c.email.trim() || c.phone.trim())
        const picCard = allCards.find(c => c.roles.includes('pic')) || allCards[0] || null

        const isSalaried = this.newClientType === 'salaried_person'
        const hideAuditor = ['salaried_person', 'partnership', 'sole_prop', 'llp'].includes(this.newClientType)
        const showCosec = this.newServiceCategories.includes('cosec')

        const companyIdentity = {
          client_type: this.newClientType,
          ...(this.newYearEnd ? { year_end: parseInt(this.newYearEnd) } : {}),
          ...(this.newTaxNumber && (this.newTaxNumber.number || this.newTaxNumber) ? { tax_number: this.newTaxNumber } : {}),
          ...(!isSalaried && this.newENumber && this.newENumber.number ? { e_number: this.newENumber } : {}),
          ...(this.newIrbBranch ? { irb_branch: this.newIrbBranch } : {}),
          ...(!hideAuditor && this.newAuditor ? { auditor: this.newAuditor } : {}),
          ...(!isSalaried && this.newSsmNumberNew.trim() ? { ssm_number_new: this.newSsmNumberNew.trim() } : {}),
          ...(!isSalaried && this.newSsmNumberOld.trim() ? { ssm_number_old: this.newSsmNumberOld.trim() } : {}),
          ...(showCosec ? {
            incorporation_date: this.parseDMY(this.newIncorporationDate) || null,
            inception_date: this.parseDMY(this.newInceptionDate) || null,
            cosec_termination_type: this.newCosecTerminationType || null,
            cosec_termination_date: this.parseDMY(this.newCosecTerminationDate) || null
          } : {}),
          category: this.buildCategoryPayload()
        }

        const data = await this.adminFetch('/api/admin/clients/create.php', {
          method: 'POST',
          body: JSON.stringify({
            client: this.newClient,
            initial: this.newInitial,
            title: picCard ? (picCard.title || '') : '',
            full_name: picCard ? picCard.name.trim() : '',
            status: this.newStatus,
            business_address: this.newBusinessAddress.trim() || null,
            mailing_address: this.newMailingAddress.trim() || null,
            contacts: allCards,
            company_identity: companyIdentity
          })
        })
        if (data === null) { this.pendingAdminRetry = () => this.addClient(); return }
        this.openPopup(data.message, 'add', 'success')
      } catch (err) {
        this.openPopup(err?.message || 'Failed to add client.', 'add', 'error')
      } finally {
        this.addingClient = false
      }
    },
    async removeClient () {
      try {
        const data = await this.adminFetch('/api/admin/clients/delete.php', {
          method: 'POST',
          body: JSON.stringify({ username: this.removeUsername })
        })
        if (data === null) { this.pendingAdminRetry = () => this.removeClient(); return }
        this.openPopup(data.message, 'remove', 'success')
      } catch (err) {
        this.openPopup(err?.message || 'Failed to remove client.', 'remove', 'error')
      }
    },
    openPopup (message, actionType = '', status = 'success') {
      this.popupMessage = message || 'Action completed.'
      this.popupActionType = actionType
      this.popupStatus = status
      this.showPopup = true
    },
    closePopup () {
      if (this.popupActionType === 'add') {
        this.newClient = ''; this.newClientType = ''; this.newYearEnd = ''
        this.newStatus = 'Active'
        this.newServiceCategories = []
        this.newTaxNumber = ''
        this.newENumber = ''
        this.newIrbBranch = ''
        this.newAuditor = ''
        this.newSsmNumberNew = ''
        this.newSsmNumberOld = ''
        this.newIncorporationDate = ''
        this.newInceptionDate = ''
        this.newCosecTerminationType = ''
        this.newCosecTerminationDate = ''
        this.newPayday = ''
        this.newInitial = ''
        this.newBusinessAddress = ''
        this.newMailingAddress = ''
        this.newContactCards = [{ label: '', title: '', name: '', phone: '', email: '', roles: [] }]
        this.formErrors = { client: '', initial: '', clientType: '', service: '', payday: '', contacts: '' }
      }
      if (this.popupActionType === 'remove') { this.removeUsername = '' }
      this.showPopup = false
      this.popupMessage = ''
      this.popupActionType = ''
      this.popupStatus = 'success'
      document.body.style.overflow = ''
    },
    onCosecFileChange (e) {
      const file = e.target.files[0]
      if (file) this.handleCosecFile(file)
      e.target.value = ''
    },
    onCosecDrop (e) {
      this.cosecIsDragging = false
      const file = e.dataTransfer.files[0]
      if (file) this.handleCosecFile(file)
    },
    async handleCosecFile (file) {
      const allowed = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']
      if (!allowed.includes(file.type) && !file.name.match(/\.xlsx?$/i)) {
        this.cosecParseError = 'Please upload a valid Excel file (.xlsx or .xls).'
        return
      }
      this.cosecParsing = true
      this.cosecParseError = ''
      this.cosecCompanies = []
      this.cosecSelected = null
      try {
        this.cosecCompanies = await parseCosecXlsx(file)
        if (!this.cosecCompanies.length) this.cosecParseError = 'No company records found in this file.'
      } catch (err) {
        this.cosecParseError = 'Failed to parse file. Make sure it matches the Cosec List format.'
        console.error(err)
      } finally {
        this.cosecParsing = false
      }
    },
    cosecPersonsByRole (company, role) {
      return (company.persons || []).filter(p => p.role === role)
    },
    async fetchClients () {
      if (this.clients.length) return
      this.clientsLoading = true
      try {
        const data = await this.adminFetch('/api/admin/clients/list.php?limit=200')
        if (data && data.success) this.clients = data.data || []
      } catch (err) {
        console.error('Failed to load clients:', err)
      } finally {
        this.clientsLoading = false
      }
    },
    async saveCosecCompany () {
      if (!this.cosecSelected || this.cosecSaving || !this.selectedClientId) return
      this.cosecSaving = true
      this.cosecSaveError = ''
      try {
        const data = await this.adminFetch('/api/admin/cosec/import.php', {
          method: 'POST',
          body: JSON.stringify({
            company: { ...this.cosecSelected, persons: undefined },
            persons: this.cosecSelected.persons,
            client_id: this.selectedClientId
          })
        })
        if (data && data.success) {
          this.cosecSaveDone = true
        } else {
          this.cosecSaveError = (data && data.message) || 'Failed to save. Please try again.'
        }
      } catch (err) {
        this.cosecSaveError = 'Network error. Please try again.'
      } finally {
        this.cosecSaving = false
      }
    },
    async fetchAdminSettings () {
      try {
        const data = await this.adminFetch('/api/admin/settings/get.php')
        if (data && data.success && data.data) {
          if (Array.isArray(data.data.irb_branches)) this.irbBranches = data.data.irb_branches
          if (Array.isArray(data.data.auditors)) this.auditors = data.data.auditors
          if (Array.isArray(data.data.contact_categories)) this.contactCategories = data.data.contact_categories
          if (Array.isArray(data.data.contact_titles)) this.contactTitles = data.data.contact_titles
        }
      } catch (err) {
        console.error('Failed to load admin settings:', err)
      }
    },
    onSettingsSaved ({ branches, auditors, categories, titles }) {
      this.irbBranches = branches
      this.auditors = auditors
      this.contactCategories = categories
      this.contactTitles = titles
    },
    resetCosecImport () {
      this.cosecCompanies = []
      this.cosecSelected = null
      this.cosecParseError = ''
      this.cosecSaveError = ''
      this.cosecSaveDone = false
      this.cosecParsing = false
      this.cosecSaving = false
      this.selectedClientId = null
    }
  }
}
</script>
